import * as yup from 'yup';
import { billingAddress as TEXT } from 'locales/en/cart';
import { fullNameRegex } from 'utils/yupHelpers';
import { escapedCountryField } from 'utils/transform';

export const FIELD_NAMES = {
  name: 'name',
  address_1: 'address_1',
  address_2: 'address_2',
  postcode: 'postcode',
  country: escapedCountryField,
  billingOption: 'billingOption',
  billingAddressSameAsShipping: 'billingAddressSameAsShipping',
  shippingMethod: 'shipping_method',
  city: 'city',
  state: 'state',
  billing_address_1: 'billing_address_1',
  billing_address_2: 'billing_address_2',
  billing_postcode: 'billing_postcode',
  billing_country: `billing_${escapedCountryField}`,
  billing_city: 'billing_city',
  billing_name: 'billing_name',
  billing_state: 'billing_state'
};

export const defaultValues = {
  [FIELD_NAMES.name]: '',
  [FIELD_NAMES.address_1]: '',
  [FIELD_NAMES.address_2]: '',
  [FIELD_NAMES.postcode]: '',
  [FIELD_NAMES.country]: {},
  [FIELD_NAMES.billingOption]: TEXT.useDifferent,
  [FIELD_NAMES.billingAddressSameAsShipping]: true,
  [FIELD_NAMES.shippingMethod]: '',
  [FIELD_NAMES.city]: '',
  [FIELD_NAMES.state]: '',
  [FIELD_NAMES.billing_name]: '',
  [FIELD_NAMES.billing_address_1]: '',
  [FIELD_NAMES.billing_address_2]: '',
  [FIELD_NAMES.billing_postcode]: '',
  [FIELD_NAMES.billing_country]: {},
  [FIELD_NAMES.billing_city]: '',
  [FIELD_NAMES.billing_state]: ''
};

const schemaObject = {
  [FIELD_NAMES.name]: yup.string().when(FIELD_NAMES.billingOption, {
    is: (val) => val !== TEXT.sameAddress,
    then: yup
      .string()
      .matches(fullNameRegex, TEXT.errors.name.fullName)
      .required(TEXT.errors.name.required),
  }),
  [FIELD_NAMES.address_1]: yup.string().when(FIELD_NAMES.billingOption, {
    is: (val) => val !== TEXT.sameAddress,
    then: yup
      .string()
      .required(TEXT.errors.address.required)
      .min(3, "Address should not be less than 3 characters")
      .max(40, 'Address should not be more than 40 characters')
  }),
  [FIELD_NAMES.address_2]: yup
    .string()
    .when(FIELD_NAMES.address_2, (val) => {
      if (val?.length) {
        return yup
          .string()
          .min(3, "Address should not be less than 3 characters")
          .max(40, 'Address should not be more than 40 characters')
      } else {
        return yup.string();
      }
    }),
  [FIELD_NAMES.postcode]: yup.string().when(FIELD_NAMES.billingOption, {
    is: (val) => val !== TEXT.sameAddress,
    then: yup.string().required(TEXT.errors.postcode.required),
  }),
  [FIELD_NAMES.country]: yup.object().when(FIELD_NAMES.billingOption, {
    is: (val) => val !== TEXT.sameAddress,
    then: yup.object().shape({
      name: yup.string().required(TEXT.errors.country.required),
      dial_code: yup.string(),
      code: yup.string(),
    }),
  }),
  [FIELD_NAMES.billingAddressSameAsShipping]: yup.boolean(),
  [FIELD_NAMES.shippingMethod]: yup.string().required('Choose a shipping method'),
  [FIELD_NAMES.city]: yup.string().required('City is required'),
  [FIELD_NAMES.state]: yup.string().required('State is required')
};

export const generateBillingAddressSchema = (nonFundraiser) => {
  if (!nonFundraiser) {
    return yup.object().shape({
      ...schemaObject,
      [FIELD_NAMES.city]: yup.string(),
      [FIELD_NAMES.state]: yup.string()
    }, [
      [FIELD_NAMES.address_2, FIELD_NAMES.address_2],
      [FIELD_NAMES.billing_address_2, FIELD_NAMES.billing_address_2]
    ]);
  };

  const mergedSchema = {
    ...schemaObject,
    [FIELD_NAMES.billing_country]: yup.object().when(FIELD_NAMES.billingOption, {
      is: (val) => val !== TEXT.sameAddress,
      then: yup.object().shape({
        name: yup.string().required(TEXT.errors.country.required),
        dial_code: yup.string(),
        code: yup.string(),
      }),
    }),
    [FIELD_NAMES.state]: yup.string().when([FIELD_NAMES.country], (country, schema) => {
      return country.code === 'US' ? schema.required('State is required') : schema.nullable()
    }),
    [FIELD_NAMES.name]: yup.string().required(TEXT.errors.name.required),
    [FIELD_NAMES.billing_name]: yup.string().required(TEXT.errors.name.required),
    [FIELD_NAMES.billing_address_1]: yup.string().when(FIELD_NAMES.billingOption, {
      is: (val) => val !== TEXT.sameAddress,
      then: yup
        .string()
        .required(TEXT.errors.address.required)
        .min(3, "Address should not be less than 3 characters")
        .max(40, 'Address should not be more than 40 characters')
    }),
    [FIELD_NAMES.billing_address_2]: yup
      .string()
      .when(FIELD_NAMES.billing_address_2, (val) => {
        if (val?.length) {
          return yup
            .string()
            .min(3, "Address should not be less than 3 characters")
            .max(40, 'Address should not be more than 40 characters')
        } else {
          return yup.string();
        }
      }),
    [FIELD_NAMES.billing_postcode]: yup.string().when(FIELD_NAMES.billingOption, {
      is: (val) => val !== TEXT.sameAddress,
      then: yup.string().required(TEXT.errors.postcode.required),
    }),
    [FIELD_NAMES.billing_city]: yup.string().required('City is required'),
    [FIELD_NAMES.billing_state]: yup.string().when([FIELD_NAMES.billing_country], (country, schema) => {
      return country.code === 'US' ? schema.required('State is required') : schema.nullable()
    }),
  };

  return yup.object().shape(mergedSchema, [
    [FIELD_NAMES.address_2, FIELD_NAMES.address_2],
    [FIELD_NAMES.billing_address_2, FIELD_NAMES.billing_address_2]
  ]);
}
